import React from 'react';
import {
  Sparkle, ChartPieSlice, Coins, UsersThree, Storefront, ClockCountdown, Armchair, EnvelopeSimple,
  PaperPlaneTilt, CheckSquareOffset, BellRinging, Buildings, Gift, Browser, Robot, SlidersHorizontal,
} from '@phosphor-icons/react';

// iOS-style light "tinted" squircle tiles with Phosphor duotone glyphs.
export const TONES = {
  rose: ['#FFE4EC', '#FFD2DF', '#D81B60'],
  sage: ['#E8F5E9', '#C8E6C9', '#2E7D32'],
  gold: ['#FFF8E1', '#FFECB3', '#D97706'],
  lavender: ['#F3E8FF', '#E9D5FF', '#7E22CE'],
  sky: ['#E0F2FE', '#BAE6FD', '#0284C7'],
  wine: ['#FCE7E9', '#F8D0D6', '#7B2C3B'],
  coral: ['#FFEDE4', '#FFDAC8', '#EA580C'],
  teal: ['#E0F7FA', '#B2EBF2', '#00838F'],
  plum: ['#F5EEF8', '#E8DAEF', '#6C3483'],
  sand: ['#F9F5EE', '#EFE6D5', '#8D6E63'],
  slate: ['#F1F5F9', '#E2E8F0', '#475569'],
  mint: ['#E6FBF5', '#C7F5E6', '#0D9488'],
};

export const FEATURE_ICONS = {
  overview: [Sparkle, 'wine'],
  insights: [ChartPieSlice, 'slate'],
  budget: [Coins, 'sage'],
  guests: [UsersThree, 'rose'],
  vendors: [Storefront, 'gold'],
  timeline: [ClockCountdown, 'lavender'],
  seating: [Armchair, 'sky'],
  savethedate: [EnvelopeSimple, 'coral'],
  invitations: [PaperPlaneTilt, 'plum'],
  rsvp: [CheckSquareOffset, 'mint'],
  reminders: [BellRinging, 'gold'],
  hotels: [Buildings, 'teal'],
  registry: [Gift, 'rose'],
  website: [Browser, 'sky'],
  ai: [Robot, 'wine'],
  settings: [SlidersHorizontal, 'sand'],
};

const SIZES = {
  xs: ['w-6 h-6', 16, '0 1px 3px rgba(0,0,0,0.06)'],
  sm: ['w-7 h-7', 19, '0 2px 6px rgba(0,0,0,0.07)'],
  md: ['w-10 h-10', 26, '0 4px 12px rgba(0,0,0,0.08)'],
  lg: ['w-14 h-14', 36, '0 8px 20px rgba(0,0,0,0.09)'],
};

export const AppIcon = ({ icon: Icon, feature, tone, size = 'md', active = false, muted = false, interactive = false, reveal = false, delay = 0, className = '', testId }) => {
  const [FIcon, fTone] = FEATURE_ICONS[feature] || [];
  const Glyph = Icon || FIcon;
  const [top, bottom, ink] = TONES[tone || fTone || 'wine'];
  const [box, px, shadow] = SIZES[size];
  const style = active
    ? { background: `linear-gradient(160deg, ${ink}, ${ink}dd)`, boxShadow: `0 6px 16px ${ink}55, inset 0 1px 0 rgba(255,255,255,0.35)`, color: '#fff' }
    : { background: `linear-gradient(160deg, ${top}, ${bottom})`, boxShadow: `${shadow}, inset 0 1px 1px rgba(255,255,255,0.95)`, border: `1px solid ${ink}22`, color: ink };
  if (muted) Object.assign(style, { filter: 'saturate(0.55)', opacity: 0.82 });
  if (reveal) style.animationDelay = `${delay}ms`;
  style['--glow'] = `${ink}40`;
  return (
    <span
      data-testid={testId}
      style={style}
      className={`relative inline-flex items-center justify-center flex-shrink-0 rounded-[28%] overflow-hidden ${box} ${reveal ? 'app-icon-reveal' : ''} ${interactive ? 'app-icon-interactive transition-[transform,box-shadow,filter,opacity] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-95' : ''} ${className}`}
    >
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-[28%] bg-gradient-to-b from-white/55 to-transparent" />
      {Glyph && <Glyph size={px} weight={active ? 'fill' : 'duotone'} className="relative [&_*[opacity]]:opacity-[0.32] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)]" />}
    </span>
  );
};

export default AppIcon;
